export interface Procedure {
  id: string;
  patientId: string;
  facilityId: string; // Required field for backend validation
  encounterId?: string;
  orderDate: Date;
  status: ProcedureStatus;
  testType: string;
  testCode: string;
  cptCode?: string;
  /**
   * Array of diagnosis sequence numbers (1-12) that support medical necessity for this procedure.
   * References EncounterDiagnosis.sequence from the encounter's diagnoses array.
   * Per HIPAA X12 837P, each service line should reference 1-4 diagnosis pointers.
   * Example: [1, 3] means this procedure is supported by diagnoses at sequence 1 and 3.
   */
  diagnosisPointers?: number[];
  labIntegrationId?: string;
  notes?: string;
  results?: ProcedureResult;
  billingStatus?: BillingStatus;
  createdAt: Date;
  updatedAt: Date;
  description?: string;
  orderingProvider?: string;
  orderingFacility?: string;
  priority?: string;
}

export interface ProcedureResult {
  id: string;
  procedureId: string;
  receivedDate: Date;
  reviewedDate?: Date;
  reviewedBy?: string;
  resultData: any; // This will be structured based on the test type
  attachments?: string[]; // URLs to result documents
}

export enum ProcedureStatus {
  PENDING = 'Pending',
  ORDERED = 'Ordered',
  COMPLETED = 'Completed',
  CANCELLED = 'Cancelled',
  SENT = 'Sent',
  RECEIVED = 'Received',
  SAVED_AS_DRAFT = 'Saved as Draft',
  COLLECTED = 'Collected',
  REVIEWED = 'Reviewed'
}

export enum BillingStatus {
  PENDING = 'PENDING',
  BILLED = 'BILLED',
  PAID = 'PAID',
  DENIED = 'DENIED'
}

export enum TestType {
  LAB = 'Lab',
  IMAGING = 'Imaging',
  PROCEDURE = 'Procedure',
  SURGICAL = 'Surgical',
  PHYSICAL_EXAM = 'Physical Exam',
  REFERRAL = 'Referral',
  GENERIC = 'Generic',
  INTERVENTION = 'Intervention',
  RISK_ASSESSMENT = 'Risk Assessment',
  MEDICATION = 'Medication' // Added for prescription flow
}

export interface TestPanel {
  id: string;
  name: string;
  description: string;
  tests: TestDefinition[];
  createdAt: Date;
  updatedAt: Date;
}

// Base interface for all test definitions
export interface BaseTestDefinition {
  id: string;
  /** Human-readable name shown in UI */
  name: string;
  /** Indicates if this can be ordered */
  orderable: boolean;
  /** Indicates if this can be reported */
  reportable: boolean;
  /** Whether the test is active */
  active: boolean;
  /** Code type for mapping (e.g., LOINC, CPT) */
  codeType: string;
  /** External mapping code value */
  codeValue: string;
  /** Test type - determines which fields are available */
  testType: TestType;
}

// Lab-specific test definition
export interface LabTestDefinition extends BaseTestDefinition {
  testType: TestType.LAB;
  /** Specimen type (e.g., Blood, Urine) */
  specimenType: string;
  /** Container type (e.g., Lavender Top Tube) */
  containerType: string;
  /** List of analytes measured */
  analytes: string[];
  /** Logic for high/low/critical alerts */
  abnormalFlags: string;
  /** Low reference range value */
  referenceRangeLow: string;
  /** High reference range value */
  referenceRangeHigh: string;
  /** Units for the result (e.g., mg/dL) */
  units: string;
}

// Imaging-specific test definition
export interface ImagingTestDefinition extends BaseTestDefinition {
  testType: TestType.IMAGING;
  /** Imaging modality (e.g., X-Ray, CT, MRI) */
  modality: string;
  /** Body region (e.g., Chest, Head) */
  bodyRegion: string;
  /** Whether contrast is required */
  contrastRequired: boolean;
  /** DICOM compatibility */
  dicomCompatible: boolean;
}

// Procedure-specific test definition
export interface ProcedureTestDefinition extends BaseTestDefinition {
  testType: TestType.PROCEDURE;
  /** Role of the performer (e.g., RN, MD) */
  performerRole: string;
  /** Estimated duration in minutes */
  estimatedDuration: number;
  /** Whether consent is required */
  requiresConsent: boolean;
  /** Location where procedure is performed */
  location: string;
  /** Special instructions */
  instructions: string;
}

// Surgical-specific test definition
export interface SurgicalTestDefinition extends BaseTestDefinition {
  testType: TestType.SURGICAL;
  /** Surgical procedure type */
  procedureType: string;
  /** Surgical specialty */
  specialty: string;
  /** Surgeon role/qualification required */
  surgeonRole: string;
  /** Anesthesia type required */
  anesthesiaType: string;
  /** Estimated duration in minutes */
  estimatedDuration: number;
  /** Whether consent is required */
  requiresConsent: boolean;
  /** Location where surgery is performed */
  location: string;
  /** Special pre-op instructions */
  preOpInstructions: string;
  /** Special post-op instructions */
  postOpInstructions: string;
  /** Whether implants are involved */
  involvesImplants: boolean;
  /** Whether blood bank preparation is needed */
  requiresBloodBank: boolean;
  /** Surgical position */
  surgicalPosition: string;
  /** Equipment requirements */
  equipmentRequired: string[];
  /** Surgical phases */
  phases: {
    preOp: boolean;
    intraOp: boolean;
    pacu: boolean;
    postOp: boolean;
  };
}

// Physical exam-specific test definition
export interface PhysicalExamTestDefinition extends BaseTestDefinition {
  testType: TestType.PHYSICAL_EXAM;
  /** Body systems examined */
  bodySystems: string[];
  /** Form template ID for documentation */
  formTemplateId: string;
  /** Role responsible for documentation */
  documentationRole: string;
  /** Sections of the exam */
  sections: string[];
}

// Referral-specific test definition
export interface ReferralTestDefinition extends BaseTestDefinition {
  testType: TestType.REFERRAL;
  /** Type of referral */
  referralType: string;
  /** Medical specialty */
  specialty: string;
  /** Method of referral communication */
  referralMethod: string;
  /** Default receiving facility information */
  defaultReceivingFacility: {
    name: string;
    npi: string;
    directAddress: string;
  };
  /** Whether authorization is required */
  requiresAuth: boolean;
  /** Default authorization instructions */
  defaultAuthInstructions: string;
  /** Whether a note is required */
  requiresNote: boolean;
  /** Default note template ID */
  defaultNoteTemplateId: string;
  /** Whether attachments are allowed */
  attachmentsAllowed: boolean;
  /** Whether tracking is enabled */
  trackingEnabled: boolean;
}

// Generic order-specific test definition
export interface GenericTestDefinition extends BaseTestDefinition {
  testType: TestType.GENERIC;
  /** Role responsible for the order */
  responsibleRole: string;
  /** Order priority */
  priority: string;
  /** Special instructions */
  instructions: string;
  /** Routing information */
  routing: {
    targetInbox: string;
    notifyProvider: boolean;
  };
}

// Risk assessment-specific test definition
export interface RiskAssessmentTestDefinition extends BaseTestDefinition {
  testType: TestType.RISK_ASSESSMENT;
  /** Assessment tool name */
  assessmentTool: string;
  /** Risk levels */
  riskLevels: string[];
  /** Scoring method */
  scoringMethod: string;
  /** Encounter types where this assessment is required */
  isRequiredForEncounterType: string[];
  /** Form template ID */
  formTemplateId: string;
  /** Outcome triggers for follow-up actions */
  outcomeTriggers: Array<{
    riskLevel: string;
    followUpAction: string;
  }>;
  /** Logic for high/low/critical alerts */
  abnormalFlags: string;
  /** Low reference range value */
  referenceRangeLow: string;
  /** High reference range value */
  referenceRangeHigh: string;
}

// Intervention-specific test definition
export interface InterventionTestDefinition extends BaseTestDefinition {
  testType: TestType.INTERVENTION;
  /** Type of intervention */
  interventionType: string;
  /** Target condition */
  targetCondition: string;
  /** Delivery mode */
  deliveryMode: string;
  /** Recommended frequency */
  recommendedFrequency: string;
  /** Documentation template ID */
  documentationTemplateId: string;
  /** Billing code */
  billingCode: string;
  /** Special instructions */
  instructions: string;
  /** Whether consent is required */
  requiresConsent: boolean;
  /** Whether attachments are allowed */
  attachmentsAllowed: boolean;
  /** Whether tracking is enabled */
  trackingEnabled: boolean;
}

// Union type for all test definitions
export type TestDefinition = 
  | LabTestDefinition 
  | ImagingTestDefinition 
  | ProcedureTestDefinition 
  | SurgicalTestDefinition
  | PhysicalExamTestDefinition 
  | ReferralTestDefinition 
  | GenericTestDefinition
  | RiskAssessmentTestDefinition
  | InterventionTestDefinition;

// Legacy interface for backward compatibility
export interface LegacyTestDefinition {
  id: string;
  /** Internal identifier, usually alphanumeric */
  testId: string;
  /** Human-readable name shown in UI */
  name: string;
  /** External mapping (LOINC, CPT, etc) */
  procedureCode: string;
  /** Code type for mapping (e.g., LOINC, CPT) */
  procedureCodeType: string;
  /** Category of the test (e.g., Laboratory Test) */
  category: string;
  /** Indicates if this can be ordered */
  orderable: boolean;
  /** Indicates if this can be reported */
  reportable: boolean;
  /** Specimen type (e.g., blood, serum, urine) */
  specimenType: string;
  /** Result data type (Numeric, Text, Coded Value, etc) */
  resultDataType: string;
  /** Units for the result (e.g., mg/dL) */
  units: string;
  /** Normal range for males */
  normalRangeM: string;
  /** Normal range for females */
  normalRangeF: string;
  /** Logic for high/low/critical alerts */
  abnormalFlags: string;
  /** Notes (e.g., fasting required) */
  notes: string;
  /** Whether the test is active */
  active: boolean;
  /** Used for formatting results display */
  resultTemplate: string;
  /** Whether this test accepts HL7 results or is manually entered */
  inputMethod: string;
  /** Associated panels (array of panel IDs) */
  associatedPanels: string[];
} 